import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Money } from "@workspace/accounting/components";
import { CheckOperation } from "@workspace/operations/check-operation";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";

import { eposApi } from "../../api";
import type { ClosedTabSummary } from "../../types";
import { useEposContext } from "../epos-context";
import { PrintTabButton } from "./print-tab-button";
import { TabOrderCompBadge } from "./tab-order-comp-badge";
import { TabPayments } from "./tab-payments";

const closedAt = (iso: string) =>
	new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * HA-360 Part B — today's closed tabs (the venue's day), newest first, from the server, each with Print Tab receipt to
 * reprint its receipt from (staff-terminal-print-receipt). A reprint of a receipt already printed is marked DUPLICATE by the
 * server. **Payments** (HA-789) shows the tab's Pay Tab payments, each refundable.
 */
export function ClosedTabsList() {
	const { tenant, venueId, operations } = useEposContext();
	const closedTabs = useQuery(eposApi.getClosedTabsOptions(tenant, venueId));
	const [paymentsOf, setPaymentsOf] = useState<ClosedTabSummary | null>(null);

	if (closedTabs.isPending) {
		return (
			<div className="flex items-center justify-center py-8">
				<Loader2 className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (closedTabs.isError) {
		return (
			<p role="alert" className="py-8 text-center text-sm text-destructive">
				Today's closed tabs couldn't be loaded.
			</p>
		);
	}

	if (closedTabs.data.length === 0) {
		return (
			<p className="py-8 text-center text-sm text-muted-foreground">
				No tabs closed today
			</p>
		);
	}

	return (
		<>
			<ul aria-label="Closed tabs" className="space-y-2">
				{closedTabs.data.map((tab) => (
					<li
						key={tab.id}
						className="flex items-center justify-between gap-2 rounded-md border px-3 py-3"
					>
						<div className="min-w-0">
							<p className="truncate font-medium">{tabLabel(tab)}</p>
							<p className="text-xs text-muted-foreground">
								Closed {closedAt(tab.closedAt)} · <Money {...tab.total} />
							</p>
						</div>
						<div className="flex gap-2">
							<Button
								variant="outline"
								aria-label={`Payments on ${tabLabel(tab)}`}
								onClick={() => setPaymentsOf(tab)}
							>
								Payments
							</Button>
							<CheckOperation
								operation="staff-terminal-print-receipt"
								operations={operations}
							>
								<PrintTabButton tabId={tab.id} label="Print Tab receipt" />
							</CheckOperation>
						</div>
					</li>
				))}
			</ul>
			<ClosedTabPaymentsDialog
				tab={paymentsOf}
				onClose={() => setPaymentsOf(null)}
			/>
		</>
	);
}

const tabLabel = (tab: ClosedTabSummary) =>
	tab.name ? `${tab.name} (Tab ${tab.number})` : `Tab ${tab.number}`;

/** HA-789 — a closed tab's Pay Tab payments, from the server, each refundable. */
function ClosedTabPaymentsDialog({
	tab,
	onClose,
}: {
	tab: ClosedTabSummary | null;
	onClose: () => void;
}) {
	const { tenant, venueId } = useEposContext();
	const detail = useQuery({
		...eposApi.getTabOptions(tenant, venueId, tab?.id ?? ""),
		enabled: !!tab,
	});

	return (
		<Dialog open={!!tab} onOpenChange={(open) => !open && onClose()}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{tab ? tabLabel(tab) : ""}</DialogTitle>
					<DialogDescription>Payments on this tab</DialogDescription>
				</DialogHeader>
				{detail.data ? (
					<>
						{/* HA-658 slice 4 — each order the tab held, with its comp badge. */}
						<ul aria-label="Orders on this tab" className="space-y-1">
							{detail.data.orders.map((line, index) => (
								<li
									key={line.id}
									className="flex items-center justify-between gap-2 text-sm"
								>
									<span className="font-medium">Round {index + 1}</span>
									<span className="flex items-center gap-2">
										<TabOrderCompBadge line={line} />
										<Money {...line.total} />
									</span>
								</li>
							))}
						</ul>
						<TabPayments detail={detail.data} />
					</>
				) : (
					<div className="flex items-center justify-center py-8">
						<Loader2 className="size-6 animate-spin text-muted-foreground" />
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
